import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Flex, Heading, Button, Input, Select, Table, Thead, Tbody, Tr, Th, Td,
  Badge, IconButton, Menu, MenuButton, MenuList, MenuItem, useDisclosure, useToast,
  HStack, Text, Spinner, SimpleGrid, Tag,
} from '@chakra-ui/react';
import {
  SearchIcon, AddIcon, DownloadIcon, ViewIcon, EditIcon, AttachmentIcon, TimeIcon, DeleteIcon,
} from '@chakra-ui/icons';
import { MdMoreVert, MdOutlinePayments, MdSearch } from 'react-icons/md';
import { GrInspect } from 'react-icons/gr';
import {
  obtenerTramitesOP, obtenerCatalogosOP, actualizarEstatusOP,
  FiltrosTramitesOP, TramiteOP, TipoTramiteOP, TipoDocumentoOP,
  obtenerTramiteOPPorId,
  descargarDocumentoOficialOP,
} from '../../../api/opTramiteApi';
import ModalTramiteOP from './components/ModalTramiteOP';
import ModalDocumentosOP from './components/ModalDocumentosOP';
import ModalHistorial from './components/ModalHistorial';
import ModalCambiarEstatusOP from './components/ModalCambiarEstatusOP';
import ModalInspeccionOP from './components/ModalInspeccionOP';
import ModalPagosOP from './components/ModalPagosOP';

const ESTATUS_COLORS: Record<string, string> = {
  REGISTRADO: 'blue', EN_PROCESO: 'cyan', EN_REVISION: 'orange',
  OBSERVADO: 'yellow', PENDIENTE_DOCUMENTACION: 'purple', PENDIENTE_PAGO: 'pink',
  EN_INSPECCION: 'teal', PROCEDENTE: 'green', NO_PROCEDENTE: 'red',
  RECHAZADO: 'red', CONCLUIDO: 'gray', CANCELADO: 'red',
};

const TIPO_DOC_COLORS: Record<string, string> = {
  LICENCIA: 'purple', PERMISO: 'teal', CONSTANCIA: 'cyan',
  AUTORIZACION: 'blue', DICTAMEN: 'orange', OFICIO: 'gray', REQUERIMIENTO: 'red',
};

interface GestionTramitesOPProps {
  embedded?: boolean;
  initialFilters?: FiltrosTramitesOP;
}

const GestionTramitesOP: React.FC<GestionTramitesOPProps> = ({ embedded = false, initialFilters }) => {
  const [tramites, setTramites] = useState<TramiteOP[]>([]);
  const [tiposTramite, setTiposTramite] = useState<TipoTramiteOP[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumentoOP[]>([]);
  const [loading, setLoading] = useState(false);
  const [filtros, setFiltros] = useState<FiltrosTramitesOP>(initialFilters || {});
  const [tramiteSeleccionado, setTramiteSeleccionado] = useState<TramiteOP | null>(null);
  const [modoModal, setModoModal] = useState<'new' | 'edit' | 'view'>('new');
  const toast = useToast();

  const { isOpen: isOpenTramite, onOpen: onOpenTramite, onClose: onCloseTramite } = useDisclosure();
  const { isOpen: isOpenDocs, onOpen: onOpenDocs, onClose: onCloseDocs } = useDisclosure();
  const { isOpen: isOpenHistorial, onOpen: onOpenHistorial, onClose: onCloseHistorial } = useDisclosure();
  const { isOpen: isOpenEstatus, onOpen: onOpenEstatus, onClose: onCloseEstatus } = useDisclosure();
  const { isOpen: isOpenInsp, onOpen: onOpenInsp, onClose: onCloseInsp } = useDisclosure();
  const { isOpen: isOpenPagos, onOpen: onOpenPagos, onClose: onClosePagos } = useDisclosure();

  const cargarCatalogos = useCallback(async () => {
    try {
      const { tiposTramite: tt, tiposDocumento: td } = await obtenerCatalogosOP();
      setTiposTramite(tt);
      setTiposDocumento(td);
    } catch {
      toast({ title: 'Error al cargar catálogos', status: 'error', duration: 3000 });
    }
  }, []);

  const cargarTramites = useCallback(async (f?: FiltrosTramitesOP) => {
    setLoading(true);
    try {
      const data = await obtenerTramitesOP(f ?? filtros);
      setTramites(data);
    } catch {
      toast({ title: 'Error al cargar trámites', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarCatalogos();
    cargarTramites(initialFilters || {});
  }, []);

  useEffect(() => {
    if (!initialFilters) return;
    setFiltros(initialFilters);
    cargarTramites(initialFilters);
  }, [JSON.stringify(initialFilters)]);

  const setFiltro = (key: keyof FiltrosTramitesOP, value: any) =>
    setFiltros(prev => ({ ...prev, [key]: value || undefined }));

  const cargarTramiteCompleto = async (tramite: TramiteOP) => {
    try {
      return await obtenerTramiteOPPorId(tramite.id);
    } catch {
      toast({ title: 'No se pudo cargar el detalle completo', status: 'warning', duration: 2500 });
      return tramite;
    }
  };

  const abrirTramite = async (tramite: TramiteOP, m: 'view' | 'edit') => {
    setTramiteSeleccionado(await cargarTramiteCompleto(tramite));
    setModoModal(m);
    onOpenTramite();
  };

  const abrirDocumentos = async (tramite: TramiteOP) => {
    setTramiteSeleccionado(await cargarTramiteCompleto(tramite));
    onOpenDocs();
  };

  const abrirNuevo = () => {
    setTramiteSeleccionado(null);
    setModoModal('new');
    onOpenTramite();
  };

  return (
    <Box p={embedded ? 0 : 4} pt={embedded ? 0 : '79px'}>
      <Flex justify="space-between" align="center" mb={5}>
        <Heading size="md" color="#5b182b">Gestión de Trámites — Obras Públicas</Heading>
        {!embedded && (
          <Button leftIcon={<DownloadIcon />} colorScheme="red" variant="outline" size="sm"
            onClick={() => window.open(`${process.env.REACT_APP_API_URL}/api/tramites-obras/reporte/pdf`, '_blank')}>
            Reporte PDF
          </Button>
        )}
      </Flex>

      {/* FILTROS */}
      <Box bg="white" p={4} borderRadius="md" shadow="sm" mb={4}>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3} mb={3}>
          <Input
            placeholder="Buscar folio, nombre, RFC..."
            value={filtros.buscar || ''}
            onChange={e => setFiltro('buscar', e.target.value)}
          />
          <Select placeholder="Tipo de Trámite" value={filtros.idTipoTramite || ''}
            onChange={e => setFiltro('idTipoTramite', e.target.value ? Number(e.target.value) : undefined)}>
            {tiposTramite.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
          </Select>
          <Select placeholder="Tipo de Documento" value={filtros.tipoDocumento || ''}
            onChange={e => setFiltro('tipoDocumento', e.target.value)}>
            {tiposDocumento.map(t => <option key={t.id} value={t.codigo}>{t.descripcion}</option>)}
          </Select>
          <Select placeholder="Estatus" value={filtros.estatus || ''}
            onChange={e => setFiltro('estatus', e.target.value)}>
            <option value="REGISTRADO">Registrado</option>
            <option value="EN_PROCESO">En Proceso</option>
            <option value="EN_REVISION">En Revisión</option>
            <option value="PROCEDENTE">Procedente</option>
            <option value="NO_PROCEDENTE">No Procedente</option>
            <option value="CONCLUIDO">Concluido</option>
            <option value="CANCELADO">Cancelado</option>
          </Select>
        </SimpleGrid>
        <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} spacing={3} mb={3}>
          <Input type="date" placeholder="Fecha inicio" value={filtros.fechaInicio || ''}
            onChange={e => setFiltro('fechaInicio', e.target.value)} />
          <Input type="date" placeholder="Fecha fin" value={filtros.fechaFin || ''}
            onChange={e => setFiltro('fechaFin', e.target.value)} />
          <Input type="number" placeholder="Año" value={filtros.anno || ''}
            onChange={e => setFiltro('anno', e.target.value ? Number(e.target.value) : undefined)} />
        </SimpleGrid>
        <HStack>
          <Button leftIcon={<SearchIcon />} colorScheme="red" size="sm" onClick={() => cargarTramites(filtros)}>
            Buscar
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setFiltros({}); cargarTramites({}); }}>
            Limpiar
          </Button>
        </HStack>
      </Box>

      {/* TABLA */}
      <Box bg="white" borderRadius="md" shadow="sm" overflowX="auto">
        {loading ? (
          <Flex justify="center" p={10}><Spinner size="xl" color="red.600" /></Flex>
        ) : (
          <Table size="sm" variant="striped">
            <Thead bg="#5b182b">
              <Tr>
                <Th color="white">Folio</Th>
                <Th color="white">Expediente</Th>
                <Th color="white">Tipo Doc</Th>
                <Th color="white">Trámite</Th>
                <Th color="white">Solicitante</Th>
                <Th color="white">Fecha</Th>
                <Th color="white">Estatus</Th>
                <Th color="white">Docs</Th>
                <Th color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {tramites.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={8}>
                    <Text color="gray.500">No se encontraron trámites</Text>
                  </Td>
                </Tr>
              ) : tramites.map(t => (
                <Tr key={t.id}>
                  <Td fontFamily="mono" fontSize="xs">{t.folio}</Td>
                  <Td fontSize="xs" color="gray.600">{t.expediente?.numeroExpediente || '—'}</Td>
                  <Td>
                    {t.tipoDocumento && (
                      <Badge colorScheme={TIPO_DOC_COLORS[t.tipoDocumento] || 'gray'} fontSize="xs">
                        {t.tipoDocumento}
                      </Badge>
                    )}
                  </Td>
                  <Td maxW="180px" isTruncated title={t.tipoTramite?.nombre}>
                    <Text fontSize="xs">{t.tipoTramite?.nombre}</Text>
                  </Td>
                  <Td>
                    <Text fontSize="xs">
                      {t.tipoPersona === 'MORAL'
                        ? (t.razonSocial || t.nombreSolicitante)
                        : [t.nombreSolicitante, t.primerApellido].filter(Boolean).join(' ')}
                    </Text>
                  </Td>
                  <Td fontSize="xs">{new Date(t.fechaRegistro).toLocaleDateString('es-MX')}</Td>
                  <Td>
                    <Badge colorScheme={ESTATUS_COLORS[t.estatus] || 'gray'} fontSize="xs">
                      {t.estatus}
                    </Badge>
                  </Td>
                  <Td fontSize="xs">{t._count?.documentos || 0}</Td>
                  <Td>
                    <Menu>
                      <MenuButton as={IconButton} icon={<MdMoreVert />} variant="ghost" size="sm" />
                      <MenuList fontSize="sm">
                        <MenuItem icon={<ViewIcon />} onClick={() => abrirTramite(t, 'view')}>Ver Detalle</MenuItem>
                        <MenuItem icon={<EditIcon />} onClick={() => abrirTramite(t, 'edit')}>Editar</MenuItem>
                        <MenuItem icon={<AttachmentIcon />} onClick={() => abrirDocumentos(t)}>
                          Documentos ({t._count?.documentos || 0})
                        </MenuItem>
                        <MenuItem icon={<TimeIcon />} onClick={() => { setTramiteSeleccionado(t); onOpenHistorial(); }}>
                          Historial
                        </MenuItem>
                        <MenuItem onClick={() => { setTramiteSeleccionado(t); onOpenEstatus(); }}>
                          Cambiar Estatus
                        </MenuItem>
                        <MenuItem icon={<GrInspect />} onClick={() => { setTramiteSeleccionado(t); onOpenInsp(); }}>
                          Inspecciones ({t._count?.inspecciones || 0})
                        </MenuItem>
                        <MenuItem icon={<MdOutlinePayments />} onClick={() => { setTramiteSeleccionado(t); onOpenPagos(); }}>
                          Pagos ({t._count?.pagos || 0})
                        </MenuItem>
                        <MenuItem icon={<DownloadIcon />} onClick={() => descargarDocumentoOficialOP(t.id)}>
                          Documento Oficial (PDF+QR)
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}
      </Box>

      {/* FAB */}
      <IconButton
        icon={<AddIcon />}
        aria-label="Nuevo Trámite"
        colorScheme="red"
        borderRadius="full"
        size="lg"
        position={embedded ? 'sticky' : 'fixed'}
        bottom={embedded ? '12px' : '24px'}
        right={embedded ? undefined : '24px'}
        float={embedded ? 'right' : undefined}
        zIndex={10}
        onClick={abrirNuevo}
        boxShadow="xl"
      />

      {/* MODALES */}
      {isOpenTramite && (
        <ModalTramiteOP
          isOpen={isOpenTramite}
          onClose={onCloseTramite}
          tramite={tramiteSeleccionado}
          tiposTramite={tiposTramite}
          tiposDocumento={tiposDocumento}
          modo={modoModal}
          onSuccess={() => { cargarTramites(); onCloseTramite(); }}
        />
      )}

      {isOpenDocs && tramiteSeleccionado && (
        <ModalDocumentosOP
          isOpen={isOpenDocs}
          onClose={onCloseDocs}
          tramite={tramiteSeleccionado}
          onSuccess={cargarTramites}
        />
      )}

      {isOpenHistorial && tramiteSeleccionado && (
        <ModalHistorial
          isOpen={isOpenHistorial}
          onClose={onCloseHistorial}
          tramiteId={tramiteSeleccionado.id}
        />
      )}

      {isOpenEstatus && tramiteSeleccionado && (
        <ModalCambiarEstatusOP
          isOpen={isOpenEstatus}
          onClose={onCloseEstatus}
          tramite={tramiteSeleccionado}
          onSuccess={() => { cargarTramites(); onCloseEstatus(); }}
        />
      )}

      {isOpenInsp && tramiteSeleccionado && (
        <ModalInspeccionOP
          isOpen={isOpenInsp}
          onClose={onCloseInsp}
          idTramite={tramiteSeleccionado.id}
          folioTramite={tramiteSeleccionado.folio}
        />
      )}

      {isOpenPagos && tramiteSeleccionado && (
        <ModalPagosOP
          isOpen={isOpenPagos}
          onClose={onClosePagos}
          idTramite={tramiteSeleccionado.id}
          folioTramite={tramiteSeleccionado.folio}
        />
      )}
    </Box>
  );
};

export default GestionTramitesOP;
